import React, { ReactElement, useContext } from 'react';
import PropTypes from 'prop-types';
import { useBrand } from '../../../BrandContext';
import { ObRedirectSpinner } from '../../../pages/Spinner';
import { useObStartData } from '../../../data/data';
import { useAggregatorConsumerName } from '../../../utilities/aggregatorConsumerNameProvider';
import { addNewRelicPageAction } from '../../../utilities/newRelic';
import ErrorContext from '../../../utilities/errorContext';
import { isAggregatorConsumer, isApiPartner } from '../../../utilities/utils';
import { safeNavigate } from '../../../utilities/safeNavigate';
import type { ObStartRequest as ObStartRequestParams } from '../../../types';

export const getQuotationToken = (agreementUrl?: string): string | undefined => {
    if (!agreementUrl) return undefined;
    try {
        return new URL(agreementUrl).searchParams.get('quotation') ?? undefined;
    } catch {
        return undefined;
    }
};

const getPcr = (pcr: string | undefined, brand: string, aggregatorConsumerName: string): string | undefined => {
    if (!isAggregatorConsumer(aggregatorConsumerName) && !isApiPartner(brand)) return undefined;
    return pcr || undefined;
};

type GetObStartParamsArgs = {
    agreementUrl?: string;
    ccsCode?: string;
    sParameter?: string;
    pcr?: string;
    brand: string;
    aggregatorConsumerName: string;
};

const getObStartParams = ({
    agreementUrl,
    ccsCode,
    sParameter,
    pcr,
    brand,
    aggregatorConsumerName
}: GetObStartParamsArgs): ObStartRequestParams | undefined => {
    const resolvedPcr = getPcr(pcr, brand, aggregatorConsumerName);
    const s = isApiPartner(brand) ? sParameter : undefined;
    const quotation = getQuotationToken(agreementUrl);
    if (quotation === undefined) return undefined;

    return {
        quotation,
        ...(ccsCode && { productCode: ccsCode }),
        ...(resolvedPcr && { pcr: resolvedPcr }),
        ...(s && { s })
    };
};

type ObStartRequestProps = {
    params: ObStartRequestParams;
    agreementUrl: string;
};

const ObStartRequest = ({ params, agreementUrl }: ObStartRequestProps): ReactElement => {
    const [data, , { error }] = useObStartData(params);

    const connectionUrl = data !== 'Session expired' && data !== undefined ? data?.connectionUrl : undefined;

    React.useEffect(() => {
        if (connectionUrl) {
            addNewRelicPageAction('OB Journey 1 Accept - Redirecting to provider');
            safeNavigate(connectionUrl);
        } else if (error) {
            addNewRelicPageAction('OB Journey 1 Accept - ob-start failed, redirecting to agreement');
            window.location.assign(agreementUrl);
        }
    }, [connectionUrl, error, agreementUrl]);

    return <ObRedirectSpinner agreementUrl={agreementUrl} />;
};

ObStartRequest.propTypes = {
    params: PropTypes.object.isRequired,
    agreementUrl: PropTypes.string.isRequired
};

type ObStartRedirectProps = {
    agreementUrl?: string;
    ccsCode?: string;
    sParameter?: string;
    pcr?: string;
};

const ObStartRedirect = ({
    agreementUrl = '',
    ccsCode = '',
    sParameter = '',
    pcr = ''
}: ObStartRedirectProps): ReactElement | null => {
    const brand = useBrand();
    const [aggregatorConsumerName] = useAggregatorConsumerName();
    const { setError } = useContext(ErrorContext);

    const params = getObStartParams({ agreementUrl, ccsCode, sParameter, pcr, brand, aggregatorConsumerName });

    React.useEffect(() => {
        if (!params?.quotation) setError();
    }, [params?.quotation, setError]);

    if (!params?.quotation) return null;

    return <ObStartRequest params={params} agreementUrl={agreementUrl} />;
};

ObStartRedirect.propTypes = {
    agreementUrl: PropTypes.string,
    ccsCode: PropTypes.string,
    sParameter: PropTypes.string,
    pcr: PropTypes.string
};

export default ObStartRedirect;
